import { Box, keyframes } from "@chakra-ui/react";
import {
  buildRouteUrl,
  investorPortalWhiteLabelRoutes,
  managerWhiteLabelSectionParams,
  managerWhiteLabelSections,
} from "@verivest/shared-components";
import { isClient } from "@verivest/shared-components/";
import {
  getBackwardAndFadeEnterAnimation,
  getBackwardAndFadeExitAnimation,
  getForwardAndFadeEnterAnimation,
  getForwardAndFadeExitAnimation,
} from "@verivest/shared-components/src/animations";
import { VeriAsyncRouteComponentType } from "@verivest/shared-components/src/defines/after-js-types";
import { useDynamicConfig } from "@verivest/shared-components/src/dynamic-config";
import DynamicConfigFlags from "@verivest/shared-components/src/dynamic-config/configs";
import React from "react";
import { ComponentTransition } from "react-component-transition";
import { Redirect } from "react-router";
import { Route, Switch, useLocation } from "react-router-dom";
import { InvestorPortalWhiteLabelPageContainer } from "../../../components/investor-portal/layouts/InvestorPortalWhiteLabelPageContainer";
import { managerWhiteLabelMenuItems } from "../../../data/menu";
import { ManagerWhiteLabelRouteParams } from "../../../routes/investorPortalPages.types";
import InvestorPortalPageNotFound from "../../investor-portal/InvestorPortalPageNotFound";
import AccountsPage from "../accounts/AccountsPage";
import { DocumentsPage } from "./DocumentsPage";
import InvestmentDetails from "./InvestmentDetails";
import { InvestmentsPage } from "./InvestmentsPage";
import { OpportunitiesPage } from "./OpportunitiesPage";
import { OpportunityPage } from "./OpportunityPage";
import { OverviewPage } from "./OverviewPage";
import { TransactionsPage } from "./TransactionsPage";
import { ManagerWhiteLabelRoutesServerInitialProps } from "./types";
import { ManagerWhiteLabelMenu } from "../../../components/investor-portal/white-label/ManagerWhiteLabelMenu";

const animations = {
  forward: {
    enterAnimation: getForwardAndFadeEnterAnimation(),
    exitAnimation: getForwardAndFadeExitAnimation(),
  },
  backward: {
    enterAnimation: getBackwardAndFadeEnterAnimation(),
    exitAnimation: getBackwardAndFadeExitAnimation(),
  },
};

const fadeInAnimationKeyframes = keyframes`
  from {transform: translateY(15px); opacity: 0;}
  to {transform: none; opacity: 1;}
`;

const fadeInAnimation = `${fadeInAnimationKeyframes} .4s ease-in-out`;

const ManagerWhiteLabelRoutes: VeriAsyncRouteComponentType<
  ManagerWhiteLabelRoutesServerInitialProps,
  ManagerWhiteLabelRouteParams
> = (props) => {
  const { managerNotFound, match } = props;

  const location = useLocation();

  const { [managerWhiteLabelSectionParams.section]: section } = match.params;

  const [prevSection, setPrevSection] = React.useState<string>(
    section || managerWhiteLabelSections.overview,
  );

  const dynamicConfig = useDynamicConfig();
  const manager = dynamicConfig.get(DynamicConfigFlags.MANAGER);

  const sectionIndex = managerWhiteLabelMenuItems.findIndex(
    (item) =>
      item.sectionName === (section || managerWhiteLabelSections.overview),
  );

  const prevSectionIndex = managerWhiteLabelMenuItems.findIndex(
    (item) =>
      item.sectionName === (prevSection || managerWhiteLabelSections.overview),
  );

  const isBackward = sectionIndex < prevSectionIndex;
  const isSameSection = sectionIndex === prevSectionIndex;

  if (managerNotFound) {
    return <InvestorPortalPageNotFound />;
  }

  if (!manager) {
    return <></>;
  }

  return (
    <InvestorPortalWhiteLabelPageContainer>
      <Box animation={fadeInAnimation}>
        <ManagerWhiteLabelMenu manager={manager} mt={10} />
        <Box>
          <ComponentTransition
            onEnterFinished={() =>
              setPrevSection(section || managerWhiteLabelSections.overview)
            }
            {...(isSameSection
              ? undefined
              : isBackward
              ? animations.backward
              : animations.forward)}
          >
            <Switch key={location.key} location={location}>
              <Route
                path={"/"}
                exact
                render={() => (
                  <Redirect
                    to={buildRouteUrl(
                      investorPortalWhiteLabelRoutes.overview,
                      {},
                    )}
                  />
                )}
              />
              <Route
                path={investorPortalWhiteLabelRoutes.overview}
                exact
                render={() => <OverviewPage manager={manager} {...props} />}
              />
              <Route
                path={investorPortalWhiteLabelRoutes.opportunities}
                exact
                render={() => (
                  <OpportunitiesPage manager={manager} {...props} />
                )}
              />
              <Route
                path={investorPortalWhiteLabelRoutes.opportunityDetails}
                exact
                render={() => <OpportunityPage manager={manager} {...props} />}
              />
              <Route
                path={investorPortalWhiteLabelRoutes.accounts}
                exact
                render={() => <AccountsPage manager={manager} {...props} />}
              />
              <Route
                path={investorPortalWhiteLabelRoutes.investments}
                exact
                render={() => <InvestmentsPage {...props} manager={manager} />}
              />
              <Route
                path={investorPortalWhiteLabelRoutes.documents}
                exact
                render={() => <DocumentsPage manager={manager} {...props} />}
              />
              <Route
                path={investorPortalWhiteLabelRoutes.transactions}
                exact
                render={() => <TransactionsPage manager={manager} {...props} />}
              />
              <Route
                path={investorPortalWhiteLabelRoutes.investmentDetails}
                exact
                render={() => (
                  <InvestmentDetails manager={manager} {...props} />
                )}
              />
            </Switch>
          </ComponentTransition>
        </Box>
      </Box>
    </InvestorPortalWhiteLabelPageContainer>
  );
};

ManagerWhiteLabelRoutes.getInitialProps = async (props) => {
  let result: ManagerWhiteLabelRoutesServerInitialProps = {};

  const { [managerWhiteLabelSectionParams.section]: section } =
    props.match.params;

  if (!isClient()) {
    if (section === managerWhiteLabelSections.overview) {
      result = { ...result, ...OverviewPage.getInitialProps?.(props) };
    }

    if (section === managerWhiteLabelSections.opportunities) {
      result = { ...result, ...OpportunitiesPage.getInitialProps?.(props) };
    }

    if (section === managerWhiteLabelSections.investments) {
      const investmentsProps = await InvestmentsPage.getInitialProps?.(props);
      result = { ...result, ...investmentsProps };
    }

    if (section === managerWhiteLabelSections.documents) {
      result = { ...result, ...DocumentsPage.getInitialProps?.(props) };
    }

    if (section === managerWhiteLabelSections.transactions) {
      result = { ...result, ...TransactionsPage.getInitialProps?.(props) };
    }
  }

  return { ...result, scrollToTop: false };
};

ManagerWhiteLabelRoutes.getChunkName = () =>
  "investor-portal-3-manager-white-label-routes-page";

export default ManagerWhiteLabelRoutes;
